import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { ProfileService } from '../../services/profile.service';
import { US_STATES } from '../../shared/constants/us-states.constant';
import {
  US_TIMEZONES,
  resolveLocationTimezone,
} from '../../shared/constants/us-timezones.constant';
import { getDefaultShopTimezone } from '../../shared/utils/date.util';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { usaZipCodeValidator } from '../../shared/validators/zipcode.validator';
import { cityStateValidator } from '../../shared/validators/location.validator';
import { Country, COUNTRIES } from '../../services/countries';
import { standardNameValidator } from '../../shared/validators/name.validator';
import { SelectSearchComponent } from '../../shared/components/select-search/select-search.component';
import { SelectFilterPipe } from '../../shared/pipes/select-filter.pipe';
import {
  ADDRESS_UPDATE_FIELDS,
  buildLocationUpdatePayload,
} from '../../shared/utils/location-details.util';

/**
 * Compact address editing form for a location: presents and validates address fields (branch, street address, city, state, country, zip)
 * and supports inline editing, change detection, save (with server update + refetch), and cancel behavior.
 * @component
 * @selector app-address-form
 * @example
 *   <app-address-form
 *     [locationDetails]="sampleLocation"
 *     [startEditable]="true"
 *     (locationUpdated)="onLocationUpdated($event)"
 *     (cancelClicked)="onCancelEdit()">
 *   </app-address-form>
 * @input {any} locationDetails - The location object used to populate the form (branch, street_address, city, state, country, zip_code). When changed, the form is patched.
 * @input {boolean} startEditable - If true the component enters edit mode on init.
 * @output {EventEmitter<any>} locationUpdated - Emitted after a successful save with the latest location details returned from the backend.
 * @output {EventEmitter<void>} cancelClicked - Emitted when the user cancels editing (also emitted after a successful save to signal closing).
 *
 * Purpose:
 *   - Provide a reactive, validated address form for a location record.
 *   - Allow toggling between read-only and edit modes, track modifications, and persist changes to the backend.
 *
 * Key properties:
 *   - addressForm: FormGroup - reactive form containing branch, street_address, city, state, country, zip_code controls with validators.
 *   - isEditing: boolean - whether the form is currently editable.
 *   - isFormModified: boolean - true when current form values differ from the original loaded values (enables Save).
 *   - isSubmitting: boolean - prevents duplicate submissions while saving.
 *   - originalFormData: any (private) - snapshot of the last committed/original form values used for change comparisons.
 *   - countries, states: arrays of available country/state options used by the UI.
 *
 * Key methods:
 *   - onSubmit(): Validates the form, builds a payload, calls the ProfileService to update location details, refetches the updated record,
 *       updates originalFormData and the form with fresh server data, emits locationUpdated, resets edit state, and displays messages.
 *   - onEdit(): Enables editing, captures the current form as originalFormData for cancel/compare, clears modification tracking.
 *   - onCancel(): Reverts the form to originalFormData, clears editing/modification state, and emits cancelClicked.
 *   - syncOriginalFormDataFromAddressForm() (private): Copies current form values into originalFormData for later comparisons or cancellation.
 *   - checkIfFormModified() (private): Compares current editable fields to originalFormData and sets isFormModified accordingly.
 *
 * Lifecycle hooks:
 *   - ngOnInit(): If startEditable is true, enters edit mode on initialization.
 *   - ngOnChanges(changes: SimpleChanges): When locationDetails input changes, patches the form with the new values,
 *       stores originalFormData (before patching) and resets modification tracking (isFormModified = false).
 *
 * Notes and behavior:
 *   - Form valueChanges are observed to update isFormModified in real time.
 *   - Save flow refetches the saved location from the API to ensure local state matches server truth; on refetch failure the component
 *     still synchronizes local originalFormData from the form to avoid losing user changes.
 *   - The component uses injected ProfileService for API calls and NotificationService for user feedback.
 */
@Component({
  selector: 'app-address-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MatSelectModule,
    SelectSearchComponent,
    SelectFilterPipe,
  ],
  templateUrl: './address-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./address-form.component.scss'],
})
export class AddressFormComponent implements OnChanges, OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  @Input() locationDetails: any;
  @Input() startEditable = false;
  addressForm: FormGroup;
  isEditing = false;
  countries: Country[] = COUNTRIES;
  isFormModified = false; // Starts as false - button disabled until changes are made
  isSubmitting = false;

  states = US_STATES;
  timezones = US_TIMEZONES;
  @Output() cancelClicked = new EventEmitter<void>();
  @Output() locationUpdated = new EventEmitter<any>();

  // Store original form values for cancellation
  private originalFormData: any = {};

  /**
   * Initializes the component: builds the reactive addressForm with validators and subscribes to valueChanges to track modifications.
   * @example
   * new AddressFormComponent(fb, profileService, messageService)
   * AddressFormComponent instance
   * @param {{FormBuilder}} {{fb}} - Angular FormBuilder used to create the reactive address form group.
   * @param {{ProfileService}} {{profileService}} - Service for loading and saving profile-related address data.
   * @param {{NotificationService}} {{messageService}} - Service for displaying user-facing messages or notifications.
   * @returns {{void}} No return value; constructs and initializes component state.
   **/
  constructor(
    private fb: FormBuilder,
    private profileService: ProfileService,
    private messageService: NotificationService
  ) {
    this.addressForm = this.fb.group({
      branch: [
        { value: '' },
        [Validators.required, Validators.maxLength(100), standardNameValidator()],
      ],
      street_address: [{ value: '' }, [Validators.required, Validators.maxLength(200)]],
      city: [{ value: '' }, [Validators.required, Validators.maxLength(100), cityStateValidator()]],
      state: [{ value: '' }, [Validators.required, Validators.maxLength(50), cityStateValidator()]],
      country: [{ value: 'USA' }, Validators.required],
      zip_code: [
        { value: '' },
        [Validators.required, Validators.maxLength(10), usaZipCodeValidator()],
      ],
      timezone: [resolveLocationTimezone(getDefaultShopTimezone()), Validators.required],
    });

    // Track form changes
    this.addressForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkIfFormModified();
    });
  }

  ngOnInit(): void {
    if (this.startEditable) {
      this.onEdit();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Synchronizes the address form when the locationDetails input changes: it prepares form data from the new location, stores the original values for later comparison, patches the form without emitting events, and resets the modified flag.
   * @example
   * gOnChanges({ locationDetails: { currentValue: { branch: 'Main', street_address: '123 St', city: 'Metropolis', state: 'NY', country: 'USA', zip_code: '12345' } } })
   * void
   * @param {SimpleChanges} changes - Changes object from Angular containing input property changes.
   * @returns {void} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locationDetails'] && this.locationDetails) {
      const formData = {
        branch: this.locationDetails.branch,
        street_address: this.locationDetails.street_address,
        city: this.locationDetails.city,
        state: this.locationDetails.state,
        country: this.locationDetails.country,
        zip_code: this.locationDetails.zip_code,
        timezone: resolveLocationTimezone(this.locationDetails.timezone),
      };

      // Store original data BEFORE patching to ensure accurate comparison
      this.originalFormData = {
        branch: this.locationDetails.branch,
        street_address: this.locationDetails.street_address,
        city: this.locationDetails.city,
        state: this.locationDetails.state,
        country: this.locationDetails.country,
        zip_code: this.locationDetails.zip_code,
        timezone: resolveLocationTimezone(this.locationDetails.timezone),
      };

      this.addressForm.patchValue(formData, { emitEvent: false });

      // Reset form modification state after loading data
      this.isFormModified = false;
    }
  }

  get branch() {
    return this.addressForm.get('branch');
  }

  get street_address() {
    return this.addressForm.get('street_address');
  }

  get city() {
    return this.addressForm.get('city');
  }

  get state() {
    return this.addressForm.get('state');
  }

  get country() {
    return this.addressForm.get('country');
  }

  get zip_code() {
    return this.addressForm.get('zip_code');
  }

  get timezone() {
    return this.addressForm.get('timezone');
  }

  /**
   * Submit the address form: validate inputs, send update to API, refetch and sync updated location details, emit events, and show notifications.
   * @example
   * nSubmit()
   * undefined
   * @param {{void}} {{none}} - No arguments.
   * @returns {{void}} No return value.
   **/
  onSubmit() {
    if (this.addressForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      const payload = buildLocationUpdatePayload(this.addressForm.value, {
        allowedFields: ADDRESS_UPDATE_FIELDS,
      });

      this.profileService.updateLocationDetails(this.locationDetails.id, payload).subscribe({
        next: () => {
          // Refetch location details from API to get the latest data
          this.profileService.getLocationDetails(this.locationDetails.id).subscribe({
            next: response => {
              if (response.status && response.data) {
                // Update original data after successful save to match current state
                const updatedLocationDetails = response.data;
                this.originalFormData = {
                  branch: updatedLocationDetails.branch,
                  street_address: updatedLocationDetails.street_address,
                  city: updatedLocationDetails.city,
                  state: updatedLocationDetails.state,
                  country: updatedLocationDetails.country,
                  zip_code: updatedLocationDetails.zip_code,
                  timezone: resolveLocationTimezone(updatedLocationDetails.timezone),
                };

                // Update form with fresh data from API
                this.addressForm.patchValue(
                  {
                    branch: updatedLocationDetails.branch,
                    street_address: updatedLocationDetails.street_address,
                    city: updatedLocationDetails.city,
                    state: updatedLocationDetails.state,
                    country: updatedLocationDetails.country,
                    zip_code: updatedLocationDetails.zip_code,
                    timezone: resolveLocationTimezone(updatedLocationDetails.timezone),
                  },
                  { emitEvent: false }
                );

                // Emit updated location details to parent component
                this.locationUpdated.emit(updatedLocationDetails);

                // Reset all modification tracking
                this.isEditing = false;
                this.isFormModified = false;
                this.isSubmitting = false;

                this.cancelClicked.emit();
                this.messageService.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: 'Location Address updated successfully',
                });
              } else {
                // If refetch fails, still update local state
                this.syncOriginalFormDataFromAddressForm();
                this.isEditing = false;
                this.isFormModified = false;
                this.isSubmitting = false;
                this.cancelClicked.emit();
                this.messageService.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: 'Location Address updated successfully',
                });
              }
            },
            error: refetchError => {
              console.error('Error refetching location details', refetchError);
              this.syncOriginalFormDataFromAddressForm();
              this.isEditing = false;
              this.isFormModified = false;
              this.isSubmitting = false;
              this.cancelClicked.emit();
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Location Address updated successfully',
              });
            },
          });
        },
        error: error => {
          this.isSubmitting = false;
          console.error('Error updating location details', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update location address. Please try again.',
          });
        },
      });
    } else {
      this.addressForm.markAllAsTouched();
    }
  }

  onEdit() {
    this.isEditing = true;
    this.syncOriginalFormDataFromAddressForm();
    this.isFormModified = false;
    this.addressForm.enable();
  }

  onCancel() {
    this.isEditing = false;
    this.isFormModified = false;
    // Restore original form data
    this.addressForm.patchValue(this.originalFormData);

    this.cancelClicked.emit();
  }

  private syncOriginalFormDataFromAddressForm(): void {
    const v = this.addressForm.value;
    this.originalFormData = {
      branch: v.branch,
      street_address: v.street_address,
      city: v.city,
      state: v.state,
      country: v.country,
      zip_code: v.zip_code,
      timezone: v.timezone,
    };
  }

  /**
   * Check if address form fields have been modified compared to originalFormData and update isFormModified.
   * @example
   * this.checkIfFormModified()
   * // this.isFormModified will be true if editable fields differ from this.originalFormData, otherwise false
   * @returns {void} Updates component state; no return value.
   */
  private checkIfFormModified() {
    // Get current form values
    const currentFormData = this.addressForm.value;

    // Compare form data fields
    const editableFields = {
      branch: currentFormData.branch,
      street_address: currentFormData.street_address,
      city: currentFormData.city,
      state: currentFormData.state,
      country: currentFormData.country,
      zip_code: currentFormData.zip_code,
      timezone: currentFormData.timezone,
    };

    // Check if form data has changed
    const formDataChanged =
      JSON.stringify(editableFields) !== JSON.stringify(this.originalFormData);

    this.isFormModified = formDataChanged;
  }
}
